import React, {Component} from 'react';
import * as firebase from 'firebase';
import {Stage as KonvaStage, Layer} from 'react-konva';
import {isMobile} from 'react-device-detect';
import ScrollButton from './scroll-button';
import ImageLayer from './image-layer';
import PatternLayer from './pattern-layer';
import ParallaxLayer from './parallax-layer';
import AnimationLayer from './animation-layer';
import ScrollingLayer from './scrolling-layer';
import './stage.css';

/** @class Stage
 * A stage is the setting for a virtual expo,
 * composed of a background and foreground.
 * WARNING: Because the stage relies on the DOM
 * Konva canvas to scale to the page, the state
 * of the Stage Component should NOT be updated.
 * Doing so will break the scaling.
 */
export default class Stage extends Component {
  /** @constructor
    * Constructs a new instance of stage
    */
  constructor(props) {
    super(props);
    this.triggerScroll = this.triggerScroll.bind(this);
    this.scrollTo = this.scrollTo.bind(this);
    this.handleResize = this.handleResize.bind(this);
    var user = firebase.auth().currentUser;
    var uid = user && user.uid;
    if(uid) {
      this.volumeRef = firebase.database().ref(`userSettings/${uid}/volume`);
      if(this.props.noise) {
        this.audio = new Audio();
        this.audio.src = this.props.noise.url;
        this.audio.loop = true;
      }
    }
  }
  /** @method componentDidMount
    * Enables the stage's background noise.
    */
  componentDidMount() {
    // Listen for volume events
    if(this.volumeRef) {
      this.volumeRef.on('value', snapshot => {
        var volume = snapshot.val();
        if(this.audio) this.audio.volume = volume;
      });
    }
    // Play background noises (if any)
    if(this.audio) this.audio.play();
    // Listen for window resize events
    window.addEventListener('resize', this.handleResize);
    // Recalculate scale
    this.handleResize();
  }
  /** @method componentWillUnmount
    * Disables the sound of the stage
    */
  componentWillUnmount() {
    // Stop listening for volume events
    if(this.volumeRef) this.volumeRef.off();
    // Stop background noises (if any)
    if(this.audio) this.audio.pause();
    // Stop listening for window resize events
    window.removeEventListener('resize', this.handleResize);
  }
  /** @method handleResize
    * Adjusts the stage to fit the available space
    */
  handleResize() {
    const width = this.wrapper.offsetWidth;
    const height = this.wrapper.offsetHeight;
    const scaleForWidth = width / this.props.width;
    const scaleForHeight =  height / this.props.height;
    const scale = Math.max(scaleForWidth, scaleForHeight);
    const scaledWidth = this.props.width * scale;
    const scaledHeight = this.props.height * scale;
    if(this.konva) {
      var stage = this.konva.getStage();
      stage.scale({x: scale, y: scale});
      stage.width(scaledWidth);
      stage.height(scaledHeight);
      stage.x((width - scaledWidth) / 2);
      stage.y((height - scaledHeight) / 2);
      stage.draw();
    }
  }
  /** @method scrollTo
    * Scrolls to the specified location.
    * @prop {Object} pos - the number of pixels to scroll in the x and y direction
    */
  scrollTo(pos) {
    var stage = this.konva.getStage();
    const maxX = stage.width() - this.wrapper.offsetWidth;
    const maxY = stage.height() - this.wrapper.offsetHeight;
    if(pos.x > 0) pos.x = 0;
    if(pos.y > 0) pos.y = 0;
    if(pos.x < -maxX) pos.x = -maxX;
    if(pos.y < -maxY) pos.y = -maxY;
    this.parallaxRefs.forEach(ref => {
      if(ref) ref.scrollTo(pos.x, pos.y, 0);
    });
    return pos;
  }
  /** @method triggerScroll
    * Triggers scrolling in the specified direction
    * @param {string} direction - should be 'up', 'down', 'left', or 'right'
    */
  triggerScroll(direction) {
    var stage = this.konva.getStage();
    const maxX = stage.width() - this.wrapper.offsetWidth;
    const maxY = stage.height() - this.wrapper.offsetHeight;
    var pos = stage.position();
    var delta = 1600;
    // eslint-disable-next-line
    switch(direction) {
      case 'up':
        pos.y = Math.min(pos.y + delta, 0);
        break;
      case 'down':
        pos.y = Math.max(pos.y - delta, -maxY);
        break;
      case 'left':
        pos.x = Math.min(pos.x + delta / 2, 0);
        break;
      case 'right':
        pos.x = Math.max(pos.x - delta / 2, -maxX);
        break;
    }
    stage.to({
      x: pos.x,
      y: pos.y,
      duration: 1.0
    });
    this.parallaxRefs.forEach(ref => {
      if(ref) ref.scrollTo(pos.x, pos.y, 1.0);
    });
  }
  /** @function
   * Renders the Stage component.
   */
  render() {
    // Generate the layers
    this.parallaxRefs = [];
    const layers = Object.values(this.props.layers)
      .sort((a,b) => {return a.order - b.order})
      .map((layer, index) => {
      switch(layer.type) {
        case 'content':
          return (<Layer key={index}>{this.props.icons}</Layer>);
        case 'image':
          return (<ImageLayer key={index} {...layer}/>);
        case 'pattern':
          return (<PatternLayer key={index} width={this.props.width} height={this.props.height} {...layer}/>)
        case 'parallax':
          return (<ParallaxLayer key={index} width={this.props.width} height={this.props.height} ref={(el) => {this.parallaxRefs.push(el);}} {...layer}/>)
        case 'scroll':
          return (<ScrollingLayer key={index} width={this.props.width} height={this.props.height} {...layer}/>);
        case 'animation':
          return (<AnimationLayer key={index} {...layer}/>);
        default:
          return null;
      }
    });
    // Generate the scroll buttons
    var scrollButtons = null;
    if(!isMobile) {
      scrollButtons = [
        <ScrollButton direction="up" key="up"
          onTrigger={()=>{this.triggerScroll("up")}} />,
        <ScrollButton direction="down" key="down"
          onTrigger={() => {this.triggerScroll("down")}}/>,
        <ScrollButton direction="left" key="left"
          onTrigger={() => {this.triggerScroll("left")}}/>,
        <ScrollButton direction="right" key="right"
          onTrigger={() => {this.triggerScroll("right")}}/>
      ]
    }
    // Render the component parts
    return (
      <div ref={div => {this.wrapper = div}} className="stage">
        <KonvaStage
          ref={(stage) => this.konva = stage}
          width={this.props.width}
          height={this.props.height}
          draggable={true}
          dragBoundFunc={this.scrollTo}
          >
          {layers}
        </KonvaStage>
        <div className="contents">
          {this.props.children}
        </div>
        {scrollButtons}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/booth-preview/stage/stage.js